import { Autocomplete, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import {
    collectEscalationCodeSuggestions, definitionsFor, escalationCodeFor,
    panelNodeKindFor, updateEscalationCode,
} from "./fluss-node-panel-utils";

interface EscalationPanelProps {
    selection: FlussElementSelection;
    modeler: any;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

export const EscalationNodePanel = ({ selection, modeler, canViewAdvanced, readOnly, onChanged }: EscalationPanelProps) => {
    const { t } = useTranslation();
    const kind = panelNodeKindFor(selection);
    const [code, setCode] = useState(escalationCodeFor(selection.businessObject));
    const [inputValue, setInputValue] = useState(code);
    const [interrupting, setInterrupting] = useState(
        Object.prototype.hasOwnProperty.call(selection.businessObject || {}, "cancelActivity") && selection.businessObject.cancelActivity === true
    );

    useEffect(() => {
        const current = escalationCodeFor(selection.businessObject);
        setCode(current);
        setInputValue(current);
        setInterrupting(Object.prototype.hasOwnProperty.call(selection.businessObject || {}, "cancelActivity") && selection.businessObject.cancelActivity === true);
    }, [selection.id, selection.businessObject]);

    const definitions = useMemo(() => definitionsFor(selection.businessObject), [selection.businessObject]);
    const suggestions = useMemo(() => collectEscalationCodeSuggestions(modeler, definitions), [modeler, definitions]);
    const label = String(t("fluss.node_panel.escalation.code", { defaultValue: "Escalation code" }));
    const commit = (raw: string) => {
        const next = raw.trim();
        setInputValue(next);
        if (next === code) return;
        setCode(next);
        updateEscalationCode(selection, modeler, next);
        onChanged();
    };

    return (
        <FlussNodePanelShell selection={selection} kind={kind} canViewAdvanced={canViewAdvanced} tabs={[{
            key: "escalation",
            label: t("fluss.node_panel.escalation.tab", { defaultValue: "Escalation" }),
            content: (
                <Stack spacing={1.25}>
                    <FlussNodePanelField label={label}>
                        <Autocomplete freeSolo size="small" disabled={readOnly} options={suggestions}
                            inputValue={inputValue} onInputChange={(_, value) => setInputValue(value)}
                            onChange={(_, value) => commit(String(value ?? ""))} onBlur={() => commit(inputValue)}
                            renderInput={(params) => <TextField {...params} inputProps={{ ...params.inputProps, "aria-label": label }} />}
                        />
                    </FlussNodePanelField>
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.node_panel.escalation.hint", {
                            defaultValue: "Leave blank to match any escalation. To capture one type only, use the same code on the throw and capture events.",
                        })}
                    </Typography>
                    {kind === "boundary_escalation" && (
                        <FormControlLabel control={<Checkbox checked={interrupting} disabled={readOnly}
                            onChange={(event) => {
                                setInterrupting(event.target.checked);
                                modeler?.get("modeling")?.updateProperties(selection.element, { cancelActivity: event.target.checked });
                                onChanged();
                            }} />}
                            label={t("fluss.node_panel.escalation.interrupt", { defaultValue: "Interrupt the activity when triggered" })}
                        />
                    )}
                </Stack>
            ),
        }]} />
    );
};
